/**
 * RecalcDiffPanel — slide-in side panel that surfaces the "what moved"
 * story after a CPM recalculate.  Anchored bottom-right so it never
 * blocks the gantt.  Auto-dismisses in 45s or on close.
 *
 * Props:
 *   open       (bool)  – visibility flag
 *   onClose    (fn)    – dismiss handler
 *   summary    ({ movedStart, movedFinish, criticalCount, elapsedMs })
 *   diffs      (array) – [{ taskId, name, wbsCode, before:{start,finish},
 *                           after:{start,finish}, dsStart, dsFinish }]
 */
import { X, TrendingUp, TrendingDown, Minus, AlertTriangle, ChevronRight } from 'lucide-react';

const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
const fmtDelta = (n) => (n > 0 ? `+${n}` : String(n));

export default function RecalcDiffPanel({ open, onClose, summary, diffs }) {
  if (!open) return null;

  const rows = (diffs || []).slice(0, 15);
  const anyMoved = rows.length > 0;

  return (
    <div className="fixed right-4 bottom-4 z-40 w-[420px] max-h-[70vh] rounded-lg border shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right-5"
         style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
         data-testid="recalc-diff-panel">

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b"
           style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-6 rounded-full" style={{ background: anyMoved ? '#f59e0b' : '#10b981' }} />
          <div>
            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Recalculate Impact · What Moved
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              {summary?.movedStart || 0} starts · {summary?.movedFinish || 0} finishes · {summary?.criticalCount || 0} critical
              {summary?.elapsedMs ? ` · ${(summary.elapsedMs / 1000).toFixed(1)}s` : ''}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-white/5"
                style={{ color: 'var(--text-muted)' }} data-testid="recalc-diff-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {!anyMoved ? (
          <div className="p-6 flex flex-col items-center justify-center text-center gap-2">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
                 style={{ background: '#10b98120', color: '#10b981' }}>
              <Minus className="w-5 h-5" />
            </div>
            <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
              No task dates changed
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              Your imported plan is already consistent with its dependencies and constraints — CPM had nothing to shift.
            </div>
          </div>
        ) : (
          <table className="w-full text-[10px]" data-testid="recalc-diff-table">
            <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
              <tr>
                <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Task</th>
                <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Δ Start</th>
                <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Δ Finish</th>
                <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Before → After</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const delta = r.dsFinish || 0;
                const color = delta > 0 ? '#ef4444' : delta < 0 ? '#10b981' : 'var(--text-muted)';
                const Icon  = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
                return (
                  <tr key={r.taskId || i} className="border-t"
                      style={{ borderColor: 'var(--border-color)' }}
                      data-testid={`recalc-diff-row-${r.taskId || i}`}>
                    <td className="p-1.5">
                      <div className="flex items-center gap-1.5 max-w-[160px]">
                        {r.wbsCode && (
                          <span className="font-mono text-[9px] shrink-0"
                                style={{ color: 'var(--accent-primary)' }}>{r.wbsCode}</span>
                        )}
                        <span className="truncate" style={{ color: 'var(--text-primary)' }}
                              title={r.name}>{r.name}</span>
                        {r.is_critical && (
                          <AlertTriangle className="w-2.5 h-2.5 shrink-0 text-red-400" title="On critical path" />
                        )}
                      </div>
                    </td>
                    <td className="p-1.5 text-right font-mono"
                        style={{ color: r.dsStart === 0 ? 'var(--text-disabled)' : color }}>
                      {r.dsStart === 0 ? '·' : fmtDelta(r.dsStart)}d
                    </td>
                    <td className="p-1.5 text-right font-mono"
                        style={{ color: r.dsFinish === 0 ? 'var(--text-disabled)' : color }}>
                      <span className="inline-flex items-center gap-0.5 justify-end">
                        {r.dsFinish !== 0 && <Icon className="w-2.5 h-2.5" />}
                        {r.dsFinish === 0 ? '·' : `${fmtDelta(r.dsFinish)}d`}
                      </span>
                    </td>
                    <td className="p-1.5 text-right font-mono"
                        style={{ color: 'var(--text-muted)', minWidth: 130 }}>
                      <div>{fmtDate(r.before?.finish)}</div>
                      <div className="flex items-center justify-end gap-0.5">
                        <ChevronRight className="w-2.5 h-2.5" />
                        <span style={{ color }}>{fmtDate(r.after?.finish)}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {diffs.length > rows.length && (
                <tr>
                  <td colSpan={4} className="p-2 text-center text-[10px]"
                      style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>
                    +{diffs.length - rows.length} more tasks shifted (see Cognara narrative for full report)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer hint */}
      {anyMoved && (
        <div className="px-3 py-1.5 border-t text-[9px] flex items-center gap-2"
             style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
          <TrendingUp className="w-3 h-3 text-red-400" />
          <span>Positive Δ = task shifted later · Negative Δ = pulled forward</span>
        </div>
      )}
    </div>
  );
}
